/**
 * 東京都の背景ツアー「東京めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - スカイツリー、東京タワー、都庁、繁華街、企業の施設はすべて入れない。
 * - 浅草寺の雷門は昭和35年の再建、五重塔は昭和48年の鉄筋コンクリート造なので入れない。
 *   二天門は慶安2年の古建築だが、浅草寺が写真の商業利用に線を引いているので今回は見送った。
 * - 明治神宮は「宣伝用の撮影」が許可制なので入れていない。皇居の二重橋は建設年が確認できなかった。
 * - 日本橋は上に首都高速道路が架かっているので、橋そのものを低い視点から描く。
 * - 浜離宮は背景に汐留の高層ビル群が入る角度を避ける。中島の御茶屋は昭和58年の再建なので描かない。
 * - 日原鍾乳洞の白衣観音は仏像なので描かない。
 * - 高尾山は薬王院を避けて、稜線と杉並木と吊り橋にする。
 * - 伊豆大島の地層大切断面は、柵の内側は見学スペースではないので人を置かない。
 * - 裏砂漠の「日本で唯一」は国土地理院の地図の表記についての限定なので、そのまま書く。
 * - 三原山は昭和61年の噴火で全島民1万人が島外へ脱出している。演出と噴火を結びつけない。
 * - 奥多摩湖は「945世帯の移転と87名の尊い犠牲のもと」完成したと東京都水道局が明記している。
 *   solemn を立て、消費的な言葉を使わない。
 */

import type { StageTour } from '../types';

export const tokyoSceneryTour: StageTour = {
  scenes: [
    {
      id: 'rikugien',
      name: '六義園のしだれ桜',
      area: '文京区',
      image: '/assets/scenery/tokyo/scenes/01_rikugien.webp',
      hotspots: [
        {
          id: 'rikugien-built',
          x: 0.25,
          y: 0.35,
          title: '7年かけて造られた',
          body: '元禄8年（1695年）に五代将軍徳川綱吉から下屋敷として与えられた駒込の地で、柳澤吉保みずから設計し指揮して、平坦な武蔵野の一隅に池を掘り山を築き、7年の歳月をかけて造園した。',
          source: 'https://www.tokyo-park.or.jp/park/rikugien/',
          mapQuery: '六義園',
        },
        {
          id: 'rikugien-meisho',
          x: 0.55,
          y: 0.65,
          title: '昭和28年に特別名勝',
          body: '昭和28年3月31日に国の特別名勝に指定された。様式は回遊式築山泉水庭園。',
          source: 'https://www.tokyo-park.or.jp/park/rikugien/',
          mapQuery: '六義園 文京区',
        },
        {
          id: 'rikugien-name',
          x: 0.8,
          y: 0.3,
          title: '名前は漢詩集から',
          body: '庭園の名は中国の古い漢詩集『毛詩』の「詩の六義」に由来し、和歌の「六体」に転用されたもの。しだれ桜は3月末に枝いっぱいの薄紅色の花を咲かせる。',
          source: 'https://www.tokyo-park.or.jp/park/rikugien/',
          mapQuery: '六義園 しだれ桜',
        },
      ],
    },
    {
      id: 'takaosan',
      name: '高尾山',
      area: '八王子市',
      image: '/assets/scenery/tokyo/scenes/02_takaosan.webp',
      hotspots: [
        {
          id: 'takaosan-height',
          x: 0.28,
          y: 0.35,
          title: '標高599メートル',
          body: '高尾山は東京都西部に位置する標高599メートルの山。長く保護されてきたため、東京近郊にありながら豊かな自然が保たれている。',
          source:
            'https://www.kankyo1.metro.tokyo.lg.jp/naturepark/know/park/introduction/toritu/meiji/characteristic.html',
          mapQuery: '高尾山',
        },
        {
          id: 'takaosan-park',
          x: 0.55,
          y: 0.62,
          title: '明治の森高尾国定公園',
          body: '1967年（昭和42年）、明治100年記念事業の一環として、都立高尾陣場自然公園の一部が明治の森高尾国定公園に指定された。面積は770ヘクタール。',
          source:
            'https://www.kankyo1.metro.tokyo.lg.jp/naturepark/know/park/introduction/toritu/meiji/history.html',
          mapQuery: '明治の森高尾国定公園',
        },
        {
          id: 'takaosan-forest',
          x: 0.8,
          y: 0.78,
          title: '二つの気候の境目',
          body: '高尾山周辺の森林は冷温帯と暖温帯の境界にあり、それぞれの気候区の生物が生育できる環境が整っている。そのため、多くの種類の動植物が生息している。',
          source:
            'https://www.kankyo1.metro.tokyo.lg.jp/naturepark/know/park/introduction/toritu/meiji/characteristic.html',
          mapQuery: '高尾山 自然研究路',
        },
      ],
    },
    {
      id: 'ogasawara',
      name: '小笠原諸島',
      area: '小笠原村',
      image: '/assets/scenery/tokyo/scenes/03_ogasawara.webp',
      hotspots: [
        {
          id: 'ogasawara-criteria',
          x: 0.28,
          y: 0.35,
          title: '一度も大陸とつながっていない島',
          body: '世界自然遺産の登録基準は「生態系」で、海洋島の著しく高い固有種率と現在進行形の生物進化が評価された。「海洋島」とは、一度も大陸と陸続きになったことがない島をいう。',
          source: 'https://www.env.go.jp/nature/isan/worldheritage/ogasawara/uiversal/index.html',
          mapQuery: '小笠原諸島',
        },
        {
          id: 'ogasawara-endemic',
          x: 0.55,
          y: 0.62,
          title: 'カタツムリは9割以上が固有種',
          body: '固有種の割合は、自生する維管束植物が約40パーセント、昆虫が約25パーセント、カタツムリは90パーセント以上で約100種にのぼる。',
          source: 'https://www.env.go.jp/nature/isan/worldheritage/ogasawara/uiversal/index.html',
          mapQuery: '父島',
        },
        {
          id: 'ogasawara-bonin',
          x: 0.8,
          y: 0.78,
          title: 'ボニンブルー',
          body: '小笠原諸島は東京から南に約1,000キロ離れており、「ボニンブルー」と呼ばれる紺碧の海と切り立った断崖に囲まれた大小30ほどの島々からなる。世界遺産の区域は聟島列島、父島列島、母島列島、北硫黄島、南硫黄島、西之島で、総面積は約7,900ヘクタール。',
          source: 'https://www.env.go.jp/nature/isan/worldheritage/ogasawara/area/index.html',
          mapQuery: '小笠原村',
        },
      ],
    },
    {
      id: 'okutamako',
      name: '奥多摩湖',
      area: '西多摩郡奥多摩町',
      image: '/assets/scenery/tokyo/scenes/04_okutamako.webp',
      solemn: true,
      hotspots: [
        {
          id: 'okutamako-spec',
          x: 0.3,
          y: 0.4,
          title: '堤高149メートル',
          body: '竣工は昭和32年11月。形式は重力式コンクリートダムで、堤高149メートル、堤頂長353メートル、有効貯水量は185,400,000立方メートル。',
          source: 'https://www.waterworks.metro.tokyo.lg.jp/kouhou/meisho/ogouchi',
          mapQuery: '小河内ダム',
        },
        {
          id: 'okutamako-cost',
          x: 0.58,
          y: 0.62,
          title: '945世帯の移転と87名の犠牲',
          body: '工事は昭和13年11月に始まり、戦争による中断を経て、昭和32年11月に、945世帯の移転と87名の尊い犠牲のもと、19年あまりの歳月と約150億円の総工費をもって完成した。',
          source: 'https://www.waterworks.metro.tokyo.lg.jp/kouhou/meisho/ogouchi',
          mapQuery: '奥多摩湖',
        },
        {
          id: 'okutamako-water',
          x: 0.8,
          y: 0.78,
          title: '都民の水の約2割',
          body: '多摩川を小河内ダムでせき止めて造られた人造湖で、東京都の貴重な水源となっている。総貯水量は1億8,000万トンで、都民が利用する水の約2割を供給している。',
          source:
            'https://www.kankyo1.metro.tokyo.lg.jp/naturepark/know/park/introduction/kokuritsu/chichibu/okutama/tourism.html',
          mapQuery: '奥多摩町 小河内',
        },
      ],
    },
    {
      id: 'urasabaku',
      name: '裏砂漠',
      area: '大島町',
      image: '/assets/scenery/tokyo/scenes/05_urasabaku.webp',
      hotspots: [
        {
          id: 'urasabaku-map',
          x: 0.3,
          y: 0.4,
          title: '地図に「砂漠」と書かれる',
          body: '国土地理院が作成する地図において、日本で唯一「砂漠」と表記される場所である。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/urasabaku.html',
          mapQuery: '裏砂漠 大島町',
        },
        {
          id: 'urasabaku-black',
          x: 0.58,
          y: 0.65,
          title: '噴火のたびに黒くなる',
          body: '火山灰とスコリアで覆われたエリアで、噴火のたびに細かい溶岩の粒や火山灰が降り積もり、黒い砂漠をつくった。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/urasabaku.html',
          mapQuery: '伊豆大島 裏砂漠',
        },
        {
          id: 'urasabaku-wind',
          x: 0.8,
          y: 0.8,
          title: '風が植物を許さない',
          body: '強い風の通り道であり、植物の種や芽生えを吹き飛ばしてしまうため、黒い景観が保たれている。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/urasabaku.html',
          mapQuery: '月と砂漠ライン',
        },
      ],
    },
    {
      id: 'chiso-daidanmen',
      name: '地層大切断面',
      area: '大島町',
      image: '/assets/scenery/tokyo/scenes/06_chiso.webp',
      hotspots: [
        {
          id: 'chiso-daidanmen-size',
          x: 0.3,
          y: 0.4,
          title: '高さ30メートル、長さ600メートル',
          body: '高さ約30メートル、長さ約600メートルにわたって続いている。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/danmen.html',
          mapQuery: '地層大切断面 大島町',
        },
        {
          id: 'chiso-daidanmen-layers',
          x: 0.58,
          y: 0.62,
          title: '150年から200年に一度の噴火',
          body: 'およそ150年から200年程度に1回という大噴火によって降り積もったスコリアや火山灰を主とする降下堆積物が幾重にも積み重なり、見事な縞模様をつくっている。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/danmen.html',
          mapQuery: '伊豆大島 地層切断面',
        },
        {
          id: 'chiso-daidanmen-shape',
          x: 0.8,
          y: 0.78,
          title: '褶曲ではない',
          body: '起伏に富み、一見すると褶曲のように見えるが、火山体の斜面の尾根と谷の繰り返し地形を覆って降り積もった堆積物を、斜面の勾配と直角に切って見せた断面である。その美しさから地元では「バームクーヘン」とも呼ばれる。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/danmen.html',
          mapQuery: '大島町 地層大切断面',
        },
      ],
    },
    {
      id: 'hamarikyu',
      name: '浜離宮恩賜庭園 潮入の池',
      area: '中央区',
      image: '/assets/scenery/tokyo/scenes/07_hamarikyu.webp',
      hotspots: [
        {
          id: 'hamarikyu-origin',
          x: 0.28,
          y: 0.35,
          title: '海を埋め立てた別邸から',
          body: '承応3年（1654年）に、四代将軍家綱の弟で甲府宰相の松平綱重が、将軍から海を埋め立てて甲府浜屋敷と呼ばれる別邸を建てる許しを得た。十一代将軍家斉のときにほぼ現在の姿の庭園が完成した。',
          source: 'https://www.tokyo-park.or.jp/park/hama-rikyu/',
          mapQuery: '浜離宮恩賜庭園',
        },
        {
          id: 'hamarikyu-special',
          x: 0.55,
          y: 0.62,
          title: '特別名勝かつ特別史跡',
          body: '昭和23年12月に国の名勝および史跡に指定され、昭和27年11月には周囲の水面を含めて国の特別名勝および特別史跡に指定された。',
          source: 'https://www.tokyo-park.or.jp/park/hama-rikyu/',
          mapQuery: '中央区 浜離宮',
        },
        {
          id: 'hamarikyu-tidal',
          x: 0.8,
          y: 0.78,
          title: 'いま海水が出入りするのはここだけ',
          body: '東京湾の海水を引く「潮入の池」で、現在、実際に海水が出入りしているのはここだけである。園内の「三百年の松」は、六代将軍家宣が庭園を大改修したときにその偉業をたたえて植えられたといわれるクロマツ。',
          source: 'https://www.tokyo-park.or.jp/park/hama-rikyu/',
          mapQuery: '浜離宮 潮入の池',
        },
      ],
    },
    {
      id: 'nihonbashi',
      name: '日本橋',
      area: '中央区',
      image: '/assets/scenery/tokyo/scenes/08_nihonbashi.webp',
      hotspots: [
        {
          id: 'nihonbashi-built',
          x: 0.28,
          y: 0.4,
          title: '明治44年竣工、国道の起点',
          body: '明治44年（1911年）竣工の石造2連アーチ橋で、我が国の国道の起点である。',
          source: 'https://online.bunka.go.jp/heritages/detail/147271',
          mapQuery: '日本橋 中央区',
        },
        {
          id: 'nihonbashi-size',
          x: 0.55,
          y: 0.62,
          title: '橋長49メートル',
          body: '橋長49メートル、橋幅28メートル、アーチ径間21メートルの規模をもつ。壁石は切石積みで、装飾用材はすべて青銅。中央および橋台部の4隅に花形ランプ付きの方錘柱を建て、各柱座に蹲踞状の麒麟を配している。',
          source: 'https://online.bunka.go.jp/heritages/detail/147271',
          mapQuery: '日本橋',
        },
        {
          id: 'nihonbashi-designation',
          x: 0.8,
          y: 0.3,
          title: '1999年に重要文化財',
          body: '重要文化財としての指定年月日は1999年5月13日。設計は米元晋一、装飾は妻木頼黄による。',
          source: 'https://online.bunka.go.jp/heritages/detail/147271',
          mapQuery: '日本橋 日本国道路元標',
        },
      ],
    },
    {
      id: 'nippara',
      name: '日原鍾乳洞',
      area: '西多摩郡奥多摩町',
      image: '/assets/scenery/tokyo/scenes/09_nippara.webp',
      hotspots: [
        {
          id: 'nippara-scale',
          x: 0.28,
          y: 0.35,
          title: '関東随一の規模とされる',
          body: '東京都指定の天然記念物。奥多摩町は関東随一の規模と紹介している。',
          source: 'https://www.town.okutama.tokyo.jp/1/kankosangyoka/kankojoho/4/1068.html',
          mapQuery: '日原鍾乳洞',
        },
        {
          id: 'nippara-temp',
          x: 0.55,
          y: 0.65,
          title: '年間を通して約11度',
          body: '洞内の気温は年間を通して約11度に保たれている。',
          source: 'https://www.town.okutama.tokyo.jp/1/kankosangyoka/kankojoho/4/1068.html',
          mapQuery: '奥多摩町 日原',
        },
        {
          id: 'nippara-growth',
          x: 0.8,
          y: 0.4,
          title: '1センチ伸びるのに約70年',
          body: '鍾乳石が1センチ伸びるために必要な年月は、およそ70年とされる。',
          source:
            'https://www.kankyo1.metro.tokyo.lg.jp/naturepark/know/park/introduction/kokuritsu/chichibu/okutama/tourism.html',
          mapQuery: '日原鍾乳洞 奥多摩',
        },
      ],
    },
    {
      id: 'miharayama',
      name: '三原山',
      area: '大島町',
      image: '/assets/scenery/tokyo/scenes/10_miharayama.webp',
      hotspots: [
        {
          id: 'miharayama-crater',
          x: 0.5,
          y: 0.35,
          title: '直径300メートル、深さ200メートル以上',
          body: '島の中央にある三原山は標高758メートルで、山頂のカルデラ内に鎮座する中央火口丘。頂部には直径300メートル、深さ200メートル以上の切り立った竪坑状の火口が口を開けている。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/mt-mihara.html',
          mapQuery: '三原山',
        },
        {
          id: 'miharayama-birth',
          x: 0.25,
          y: 0.68,
          title: '1777年の噴火で生まれた',
          body: '1777年に始まった安永噴火で誕生した。日本の活火山のなかでも玄武岩質マグマの活発なことで知られ、およそ35年以内に一度は比較的大きな噴火を行う。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/mt-mihara.html',
          mapQuery: '伊豆大島 三原山',
        },
        {
          id: 'miharayama-1986',
          x: 0.78,
          y: 0.7,
          title: '昭和61年の全島避難',
          body: '昭和61年11月15日から始まった噴火は、玄武岩質のマグマを連続的に噴き上げる溶岩噴泉の活動に始まり、21日には割れ目噴火により外輪山にも11個の火口ができ、この日、全島民1万人が島外へ脱出した。',
          source: 'https://www.town.oshima.tokyo.jp/soshiki/kankou/mt-mihara.html',
          mapQuery: '三原山山頂口',
        },
      ],
    },
  ],
};
